import React from "react";
import { useNavigate, useParams } from "react-router";
import { LongTextLabel, Toast } from "@sscs/ui";
import { Dialog } from "Components";
import { STORE_PROJECT_ID } from "Constants";
import { useProjectsRoute, useReports } from "Hooks";
import { ProjectSummary } from "Types";
import { apiErrorToast } from "Utils";
import { useDeleteProjectMutation } from "../../services/projects.api";
import styles from "./deleteProjectDialog.module.scss";

type DeleteProjectDialogProps = {
  project: ProjectSummary;
  isOpen: boolean;
  onClose: () => void;
};

const DeleteProjectDialog = ({
  project,
  isOpen,
  onClose,
}: DeleteProjectDialogProps): React.JSX.Element => {
  const { groupId } = useParams();
  const { closeAllReports } = useReports();
  const projectsRoute = useProjectsRoute();
  const navigate = useNavigate();
  const [deleteProject] = useDeleteProjectMutation();

  const handleDelete = async () => {
    if (project) {
      try {
        await deleteProject({
          projectId: project.project_id,
        }).unwrap();

        localStorage.removeItem(STORE_PROJECT_ID);

        navigate(projectsRoute);

        Toast.show({
          title: "Project deleted",
          type: "success",
        });

        // Close all reports belonging to this project
        closeAllReports({ groupId, projectName: project.name });
      } catch (error) {
        apiErrorToast(error, "Could not delete project");
      }
    }

    onClose();
  };

  return (
    <Dialog
      title={`Delete project ${project.name}?`}
      confirmText="Yes, delete this project"
      isOpen={isOpen}
      onCancel={onClose}
      onClose={onClose}
      onConfirm={handleDelete}
      cancelButtonColor="primary"
      confirmButtonColor="error"
      dangerZone={project.name}
    >
      <div>
        <div className={styles.content}>
          Do you want to delete project{" "}
          <LongTextLabel
            label={project.name}
            className={styles["delete-project"]}
          />
          ?
        </div>
        If the project is deleted{" "}
        <b>
          {project.packages.length.toLocaleString()} package
          {project.packages.length !== 1 ? "s" : ""}
        </b>{" "}
        and {project.packages.length > 1 ? "all their" : "its"} content will be
        deleted.
      </div>
    </Dialog>
  );
};

export default DeleteProjectDialog;
